import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import KpiCard from './KpiCard';
import { VALET, FONTS, TYPE } from '../../theme';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const SectionEyebrow = styled(Typography)({
  ...TYPE.eyebrow,
  marginBottom: '6px',
});

const SectionTitle = styled(Typography)({
  ...TYPE.sectionTitle,
  marginBottom: '16px',
});

const Grid4 = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '14px',
  marginBottom: '28px',
  '@media (max-width: 900px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
});

const Grid2 = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '14px',
  marginBottom: '28px',
  // Two charts side by side is ~170px each on a phone, which is not a chart.
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
  },
});

const ChartCard = styled(Box)({
  backgroundColor: VALET.surface,
  border: `1px solid ${VALET.border}`,
  borderRadius: '14px',
  padding: '22px',
  boxShadow: VALET.shadow,
  '@media (max-width: 768px)': {
    padding: '16px 14px',
  },
});

const CHART_GRID = `${VALET.bronze}22`;
const CHART_TICK = VALET.textMuted;

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      labels: { color: CHART_TICK, font: { size: 11, family: 'Inter' }, boxWidth: 10, boxHeight: 10 },
    },
    tooltip: {
      backgroundColor: VALET.ink,
      padding: 10,
      titleFont: { size: 11, family: 'Inter', weight: '600' },
      bodyFont: { size: 11, family: 'Inter' },
      cornerRadius: 6,
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: CHART_TICK, font: { size: 10, family: 'Inter' }, maxRotation: 0 },
    },
    y: {
      grid: { color: CHART_GRID },
      ticks: { color: CHART_TICK, font: { size: 10, family: 'Inter' } },
      border: { dash: [3, 3] },
    },
  },
};

export default function BusinessTab({ data }) {
  if (!data) return null;
  const d = data;

  const revenueData = {
    labels: ['Gross Revenue', 'Net Revenue'],
    datasets: [
      {
        label: 'Gross',
        data: [parseRevenue(d.grossRevenue), 0],
        backgroundColor: `${VALET.green}22`,
        borderColor: VALET.green,
        borderWidth: 1.5,
        borderRadius: 4,
      },
      {
        label: 'Net',
        data: [0, parseRevenue(d.netRevenue)],
        backgroundColor: `${VALET.bronze}22`,
        borderColor: VALET.bronze,
        borderWidth: 1.5,
        borderRadius: 4,
      },
    ],
  };

  return (
    <Box>
      <SectionEyebrow>Overview</SectionEyebrow>
      <SectionTitle>How the business is doing</SectionTitle>
      <Grid4>
        <KpiCard label="Total requests" value={d.totalRequests} subtitle="all service types" />
        <KpiCard label="Completed jobs" value={d.completedJobs} subtitle="↑ completion" subtitleColor="pos" />
        <KpiCard label="Completion rate" value={d.completionRate} subtitle="target > 85%" subtitleColor="pos" />
        <KpiCard label="Cancellation rate" value={d.cancellationRate} subtitle="target < 10%" subtitleColor={getCancelColor(d.cancellationRate)} />
      </Grid4>

      <Grid4>
        <KpiCard label="Gross revenue" value={d.grossRevenue} subtitle="before deductions" />
        <KpiCard label="Net revenue" value={d.netRevenue} subtitle="after ops costs" />
        <KpiCard label="Active users" value={d.activeUsers} subtitle="unique this period" />
        <KpiCard label="Active valets" value={d.activeValets} subtitle="on platform" />
      </Grid4>

      <SectionEyebrow>Operations</SectionEyebrow>
      <SectionTitle>Service speed</SectionTitle>
      <Grid2>
        <KpiCard label="Avg pickup ETA" value={d.avgPickupETA} subtitle="dispatch to arrival" />
        <KpiCard label="Avg retrieval time" value={d.avgRetrieveTime} subtitle="request to delivery" />
      </Grid2>

      <SectionEyebrow>Revenue</SectionEyebrow>
      <SectionTitle>Gross vs net this period</SectionTitle>
      <ChartCard>
        <Box sx={{ height: 200 }}>
          <Bar data={revenueData} options={chartOptions} />
        </Box>
      </ChartCard>
    </Box>
  );
}

function parseRevenue(str) {
  if (typeof str === 'number') return str;
  if (!str) return 0;
  return Number(String(str).replace(/[^0-9.-]/g, '')) || 0;
}

function getCancelColor(rate) {
  const val = parseFloat(rate);
  return val > 10 ? 'neg' : 'neu';
}
